import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
  Badge,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Receipt,
  Payment,
  CreditCard,
  AccountBalance,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";
import { useNotification } from "../../components/common/NotificationProvider";
import { useFormValidation, commonValidationRules } from "../../hooks/useFormValidation";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Billing: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const { invoices, addInvoice, updateInvoice, deleteInvoice } = useStore();
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingInvoice, setEditingInvoice] = useState<any>(null);
  const [formData, setFormData] = useState({
    patient: "",
    amount: "",
    date: "",
    dueDate: "",
    status: "Pending",
    paymentMethod: "",
    insuranceProvider: "",
  });

  const validationRules = {
    patient: commonValidationRules.name,
    amount: commonValidationRules.amount,
    date: commonValidationRules.date,
    dueDate: commonValidationRules.date,
    paymentMethod: { required: true },
    insuranceProvider: { required: true },
  };

  const { errors, validateForm, validateFieldOnChange, clearErrors } = useFormValidation(validationRules);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: string, invoice?: any) => {
    setDialogType(type);
    if (invoice) {
      setEditingInvoice(invoice);
      setFormData({
        patient: invoice.patient,
        amount: invoice.amount.toString(),
        date: invoice.date,
        dueDate: invoice.dueDate,
        status: invoice.status,
        paymentMethod: invoice.paymentMethod,
        insuranceProvider: invoice.insuranceProvider,
      });
    } else {
      setEditingInvoice(null);
      setFormData({
        patient: "",
        amount: "",
        date: "",
        dueDate: "",
        status: "Pending",
        paymentMethod: "",
        insuranceProvider: "",
      });
    }
    clearErrors();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType("");
    setEditingInvoice(null);
    setFormData({
      patient: "",
      amount: "",
      date: "",
      dueDate: "",
      status: "Pending",
      paymentMethod: "",
      insuranceProvider: "",
    });
    clearErrors();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    validateFieldOnChange(field, value);
  };

  const handleSubmit = () => {
    if (!validateForm(formData)) {
      showError("Please fix the form errors before submitting");
      return;
    }

    try {
      const invoiceData = {
        patient: formData.patient,
        amount: parseFloat(formData.amount),
        date: formData.date,
        dueDate: formData.dueDate,
        status: formData.status,
        paymentMethod: formData.paymentMethod,
        insuranceProvider: formData.insuranceProvider,
        invoiceNumber: editingInvoice?.invoiceNumber || `INV-${Date.now()}`,
      };

      if (editingInvoice) {
        updateInvoice(editingInvoice.id, invoiceData);
        showSuccess("Invoice updated successfully");
      } else {
        addInvoice(invoiceData);
        showSuccess("Invoice created successfully");
      }
      handleCloseDialog();
    } catch (error) {
      showError("Failed to save invoice");
    }
  };

  const handleDelete = (id: number) => {
    try {
      deleteInvoice(id);
      showSuccess("Invoice deleted successfully");
    } catch (error) {
      showError("Failed to delete invoice");
    }
  };

  // Mock data
  const invoicesData = [
    {
      id: 1,
      invoiceNumber: "INV-2024-001",
      patient: "Ahmed Al-Rashid",
      amount: 2500,
      date: "2024-01-15",
      dueDate: "2024-02-15",
      status: "Paid",
      paymentMethod: "Insurance",
      insuranceProvider: "Tawuniya",
    },
    {
      id: 2,
      invoiceNumber: "INV-2024-002",
      patient: "Fatima Hassan",
      amount: 1800,
      date: "2024-01-14",
      dueDate: "2024-02-14",
      status: "Pending",
      paymentMethod: "Cash",
      insuranceProvider: "N/A",
    },
    {
      id: 3,
      invoiceNumber: "INV-2024-003",
      patient: "Omar Al-Zahra",
      amount: 3200,
      date: "2024-01-16",
      dueDate: "2024-02-16",
      status: "Overdue",
      paymentMethod: "Insurance",
      insuranceProvider: "Bupa",
    },
  ];

  const paymentsData = [
    {
      id: 1,
      paymentNumber: "PAY-2024-001",
      patient: "Ahmed Al-Rashid",
      amount: 2500,
      date: "2024-01-15",
      method: "Insurance",
      status: "Completed",
      reference: "TWN-123456",
    },
    {
      id: 2,
      paymentNumber: "PAY-2024-002",
      patient: "Fatima Hassan",
      amount: 1800,
      date: "2024-01-14",
      method: "Cash",
      status: "Completed",
      reference: "CASH-001",
    },
    {
      id: 3,
      paymentNumber: "PAY-2024-003",
      patient: "Omar Al-Zahra",
      amount: 3200,
      date: "2024-01-16",
      method: "Credit Card",
      status: "Processing",
      reference: "CC-789012",
    },
  ];

  const insuranceClaimsData = [
    {
      id: 1,
      claimNumber: "CLM-2024-001",
      patient: "Ahmed Al-Rashid",
      insuranceProvider: "Tawuniya",
      amount: 2500,
      submittedDate: "2024-01-15",
      status: "Approved",
      approvalDate: "2024-01-18",
      paymentDate: "2024-01-20",
    },
    {
      id: 2,
      claimNumber: "CLM-2024-002",
      patient: "Fatima Hassan",
      insuranceProvider: "Bupa",
      amount: 1800,
      submittedDate: "2024-01-14",
      status: "Under Review",
      approvalDate: null,
      paymentDate: null,
    },
    {
      id: 3,
      claimNumber: "CLM-2024-003",
      patient: "Omar Al-Zahra",
      insuranceProvider: "MedGulf",
      amount: 3200,
      submittedDate: "2024-01-16",
      status: "Rejected",
      approvalDate: null,
      paymentDate: null,
    },
  ];

  const revenueCycleData = [
    {
      period: "January 2024",
      totalRevenue: 2450000,
      insuranceRevenue: 1800000,
      cashRevenue: 450000,
      outstandingReceivables: 200000,
      collectionRate: 92.5,
    },
    {
      period: "December 2023",
      totalRevenue: 2200000,
      insuranceRevenue: 1600000,
      cashRevenue: 400000,
      outstandingReceivables: 180000,
      collectionRate: 90.2,
    },
    {
      period: "November 2023",
      totalRevenue: 2100000,
      insuranceRevenue: 1500000,
      cashRevenue: 450000,
      outstandingReceivables: 150000,
      collectionRate: 91.8,
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Paid":
      case "Completed":
      case "Approved":
        return "success";
      case "Pending":
      case "Processing":
      case "Under Review":
        return "warning";
      case "Overdue":
      case "Rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case "Insurance":
        return <AccountBalance />;
      case "Credit Card":
        return <CreditCard />;
      case "Cash":
        return <AttachMoney />;
      default:
        return <Payment />;
    }
  };

  const overdueInvoices = invoicesData.filter(
    (invoice) => invoice.status === "Overdue"
  );
  const pendingClaims = insuranceClaimsData.filter(
    (claim) => claim.status === "Under Review"
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("billing.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Comprehensive billing and revenue cycle management
      </Typography>

      {/* Alerts */}
      {overdueInvoices.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Overdue Invoices
          </Typography>
          {overdueInvoices.map((invoice) => (
            <Typography key={invoice.id} variant="body2">
              • {invoice.invoiceNumber} - {invoice.patient} (SAR{" "}
              {invoice.amount.toLocaleString()})
            </Typography>
          ))}
        </Alert>
      )}

      {pendingClaims.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Pending Insurance Claims
          </Typography>
          {pendingClaims.map((claim) => (
            <Typography key={claim.id} variant="body2">
              • {claim.claimNumber} - {claim.patient} (SAR{" "}
              {claim.amount.toLocaleString()})
            </Typography>
          ))}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Receipt color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">SAR 2.45M</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Revenue
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Payment color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">92.5%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Collection Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccountBalance color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">SAR 200K</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Outstanding AR
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TrendingUp color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">+12.5%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Revenue Growth
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Invoices" />
            <Tab label="Payments" />
            <Tab label="Insurance Claims" />
            <Tab label="Revenue Cycle" />
            <Tab label="Reports" />
          </Tabs>
        </Box>

        {/* Invoices Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Invoice Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("invoice")}
            >
              Create Invoice
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Insurance Provider</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell>{invoice.patient}</TableCell>
                    <TableCell align="right">
                      SAR {invoice.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{invoice.date}</TableCell>
                    <TableCell>{invoice.dueDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status}
                        color={getStatusColor(invoice.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{invoice.paymentMethod}</TableCell>
                    <TableCell>{invoice.insuranceProvider}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog("invoice", invoice)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(invoice.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Payments Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Payment Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("payment")}
            >
              Record Payment
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Payment #</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentsData.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{payment.paymentNumber}</TableCell>
                    <TableCell>{payment.patient}</TableCell>
                    <TableCell align="right">
                      SAR {payment.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{payment.date}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {getPaymentMethodIcon(payment.method)}
                        <Typography sx={{ ml: 1 }}>{payment.method}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={payment.status}
                        color={getStatusColor(payment.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{payment.reference}</TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Insurance Claims Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Insurance Claims</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("claim")}
            >
              Submit Claim
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Claim #</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Insurance Provider</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Submitted Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Approval Date</TableCell>
                  <TableCell>Payment Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {insuranceClaimsData.map((claim) => (
                  <TableRow key={claim.id}>
                    <TableCell>{claim.claimNumber}</TableCell>
                    <TableCell>{claim.patient}</TableCell>
                    <TableCell>{claim.insuranceProvider}</TableCell>
                    <TableCell align="right">
                      SAR {claim.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{claim.submittedDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={claim.status}
                        color={getStatusColor(claim.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{claim.approvalDate || "-"}</TableCell>
                    <TableCell>{claim.paymentDate || "-"}</TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Revenue Cycle Tab */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Revenue Cycle Management
          </Typography>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Total Revenue</TableCell>
                  <TableCell align="right">Insurance Revenue</TableCell>
                  <TableCell align="right">Cash Revenue</TableCell>
                  <TableCell align="right">Outstanding AR</TableCell>
                  <TableCell align="right">Collection Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revenueCycleData.map((revenue, index) => (
                  <TableRow key={index}>
                    <TableCell>{revenue.period}</TableCell>
                    <TableCell align="right">
                      SAR {revenue.totalRevenue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      SAR {revenue.insuranceRevenue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      SAR {revenue.cashRevenue.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      SAR {revenue.outstandingReceivables.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {revenue.collectionRate}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Reports Tab */}
        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" gutterBottom>
            Billing Reports
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Revenue Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Monthly and quarterly revenue analysis
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Aging Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Accounts receivable aging analysis
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Insurance Claims Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Insurance claims status and performance
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Dialog for adding new items */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "invoice" && (editingInvoice ? "Edit Invoice" : "Create New Invoice")}
          {dialogType === "payment" && "Record Payment"}
          {dialogType === "claim" && "Submit Insurance Claim"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogType === "invoice" ? (
              <>
                <TextField
                  fullWidth
                  label="Patient Name"
                  variant="outlined"
                  value={formData.patient}
                  onChange={(e) => handleInputChange("patient", e.target.value)}
                  error={!!errors.patient}
                  helperText={errors.patient}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  variant="outlined"
                  value={formData.amount}
                  onChange={(e) => handleInputChange("amount", e.target.value)}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  variant="outlined"
                  value={formData.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  error={!!errors.date}
                  helperText={errors.date}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  variant="outlined"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange("dueDate", e.target.value)}
                  error={!!errors.dueDate}
                  helperText={errors.dueDate}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Payment Method</InputLabel>
                  <Select 
                    label="Payment Method"
                    value={formData.paymentMethod}
                    onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
                  >
                    <MenuItem value="Insurance">Insurance</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Credit Card">Credit Card</MenuItem>
                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Insurance Provider</InputLabel>
                  <Select 
                    label="Insurance Provider"
                    value={formData.insuranceProvider}
                    onChange={(e) => handleInputChange("insuranceProvider", e.target.value)}
                  >
                    <MenuItem value="Tawuniya">Tawuniya</MenuItem>
                    <MenuItem value="Bupa">Bupa</MenuItem>
                    <MenuItem value="MedGulf">MedGulf</MenuItem>
                    <MenuItem value="N/A">N/A</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select 
                    label="Status"
                    value={formData.status}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Overdue">Overdue</MenuItem>
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Patient Name"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Payment Method</InputLabel>
                  <Select label="Payment Method">
                    <MenuItem value="insurance">Insurance</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="credit">Credit Card</MenuItem>
                    <MenuItem value="bank">Bank Transfer</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={dialogType === "invoice" ? handleSubmit : handleCloseDialog}>
            {dialogType === "invoice" ? (editingInvoice ? "Update" : "Create") : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;
